// @/components/layout/mobile-drawer.jsx
// Mobile navigation drawer using HeadlessUI Dialog
// Slides in from the left with navigation widget

import React, { useEffect } from "react";
import * as Headless from "@headlessui/react";
import { AnimatePresence, motion } from "motion/react";
import { useLocation } from "@reach/router";

import { useLayout } from "./main";
import { Button } from "@/components/reusable/button";
import NavigationWidget from "./navigation-widget";

// ============================================================================
// ICONS
// ============================================================================

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-6"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// ANIMATION CONFIG
// ============================================================================

const drawerSpring = {
  type: "spring",
  damping: 28,
  stiffness: 300,
  mass: 0.6,
};

// ============================================================================
// MOBILE DRAWER
// ============================================================================

const MobileDrawer = () => {
  const { isMobileMenuOpen, closeMobileMenu } = useLayout();
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => {
    if (isMobileMenuOpen) {
      closeMobileMenu();
    }
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {isMobileMenuOpen && (
        <Headless.Dialog
          static
          open={isMobileMenuOpen}
          onClose={closeMobileMenu}
          className="relative z-50"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-xs"
            aria-hidden="true"
          />

          {/* Drawer panel */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={drawerSpring}
            className="fixed inset-y-0 left-0 w-full max-w-80 p-2"
          >
            <Headless.DialogPanel className="flex h-full flex-col rounded-2xl bg-zinc-50 shadow-xl ring-1 ring-black/5">
              {/* Close button */}
              <div className="absolute top-4 right-4 z-10">
                <Button
                  variant="text"
                  size="icon"
                  onClick={closeMobileMenu}
                  className="p-2! rounded-xl! text-zinc-500 hover:text-zinc-900 hover:bg-white/80 transition-colors"
                  aria-label="Close menu"
                >
                  <CloseIcon />
                </Button>
              </div>

              {/* Navigation content */}
              <div className="flex-1 overflow-hidden">
                <MobileNavigationWidget />
              </div>
            </Headless.DialogPanel>
          </motion.div>
        </Headless.Dialog>
      )}
    </AnimatePresence>
  );
};

// Nav links only (logo & user are in header)
const MobileNavigationWidget = () => (
  <div className="h-full pt-4" data-mobile-nav="true">
    <NavigationWidget forceExpanded hideHeaderFooter />
  </div>
);

export default MobileDrawer;
